import { InputLabel, Select, FormControl, IconButton } from "@material-ui/core";
import { DeleteForeverOutlined } from "@material-ui/icons";
import { isNil } from "lodash";

import { Trans } from "../utils/i18n";
import { formatCurrency } from "../utils/formatCurrency";
import { ImagePlaceholder } from "./ImagePlaceholder";
import classNames from "classnames";

export function ShoppingCartRoot({ children, className }) {
    return (
        <div className={classNames("flex flex-col gap-4 @container", className)}>
            <div className="hidden @3xl:grid grid-cols-12 gap-10 items-center">
                <div className="col-span-5 font-bold">
                    <Trans>Product</Trans>
                </div>
                <div className="col-span-3 font-bold">
                    <Trans>Quantity</Trans>
                </div>
                <div className="col-span-2 font-bold">
                    <Trans>Price</Trans>
                </div>
                <div className="col-span-2 font-bold">
                    <Trans>Total</Trans>
                </div>
            </div>

            <div className="divide-y divide-gray-200 [&>*]:py-4 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
                {children}
            </div>
        </div>
    );
}

export function ShoppingCartProductRow({
    image,
    showImage = true,
    title,
    quantity,
    maxQuantity,
    price,
    showQuantity = true,
    showRemoveButton = true,
    onRemove = () => {},
    onUpdateQuantity = () => {},
    showNetPrice = true,
    netPriceLabel,
    showNetPriceTotal = true,
    netPriceTotalLabel,
}) {
    const finalMaxQuantity =
        isNil(maxQuantity) || isNaN(maxQuantity) ? quantity ?? 1 : maxQuantity;

    return (
        <div className="grid grid-cols-12 gap-4 @3xl:gap-10 items-center">
            <div className="col-span-12 @3xl:col-span-5">
                <div className="flex items-center gap-4">
                    {showImage && (
                        <div className="flex items-center justify-center size-20 shrink-0 overflow-hidden rounded-md">
                            {image ? (
                                <img
                                    src={image}
                                    alt={title}
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <ImagePlaceholder className="!w-full !h-full bg-gray-300 shrink-0" />
                            )}
                        </div>
                    )}

                    <span className="flex-1 font-semibold">{title}</span>
                </div>
            </div>
            <div className="col-span-12 @3xl:col-span-3">
                <div className="flex items-center gap-4">
                    {showQuantity && (
                        <FormControl variant="outlined" size="small">
                            <InputLabel id="quantity-label">
                                <Trans i18nKey="orderEntry.quantity.label">
                                    Amount
                                </Trans>
                            </InputLabel>
                            <Select
                                labelId="quantity-label"
                                label={
                                    <Trans i18nKey="orderEntry.quantity.label">
                                        Amount
                                    </Trans>
                                }
                                size="small"
                                onChange={(e) =>
                                    onUpdateQuantity(e.target.value)
                                }
                                value={quantity}
                                native
                            >
                                {[...Array(finalMaxQuantity).keys()].map(
                                    (item) => {
                                        // Start from quantity = 1
                                        item += 1;
                                        return (
                                            <option
                                                key={String(item)}
                                                value={String(item)}
                                            >
                                                {String(item)}
                                            </option>
                                        );
                                    }
                                )}
                            </Select>
                        </FormControl>
                    )}

                    {showRemoveButton && (
                        <IconButton
                            aria-label="Remove product"
                            onClick={() => onRemove()}
                        >
                            <DeleteForeverOutlined className="text-red-500" />
                        </IconButton>
                    )}
                </div>
            </div>
            {showNetPrice && (
                <div className="col-span-12 @3xl:col-span-2">
                    <span className="font-bold @3xl:hidden">
                        {netPriceLabel || <Trans>Price</Trans>}:{" "}
                    </span>
                    {formatCurrency(price?.price, price?.currency)}
                </div>
            )}
            {showNetPriceTotal && (
                <div className="col-span-12 @3xl:col-span-2">
                    <span className="font-bold @3xl:hidden">
                        {netPriceTotalLabel || <Trans>Total</Trans>}:{" "}
                    </span>
                    {formatCurrency(
                        price?.price * Number(quantity),
                        price?.currency
                    )}
                </div>
            )}
        </div>
    );
}

export function ShoppingCartPriceSummaryRow({ title, price }) {
    return (
        <div className="grid grid-cols-12 gap-4 @3xl:gap-10 items-center">
            <div className="hidden @3xl:block @3xl:col-span-3" />
            <div className="hidden @3xl:block @3xl:col-span-4" />
            <div className="col-span-12 text-right @3xl:text-start @3xl:col-span-3">
                {title}
            </div>
            <div className="col-span-12 text-right @3xl:text-start @3xl:col-span-2">
                {price}
            </div>
        </div>
    );
}

export const ShoppingCart = {
    Root: ShoppingCartRoot,
    ProductRow: ShoppingCartProductRow,
    PriceSummaryRow: ShoppingCartPriceSummaryRow,
};
